import { Injectable, signal, computed, inject, DestroyRef } from '@angular/core';
import { signInWithEmailAndPassword, signInWithCustomToken, signOut, onIdTokenChanged, User } from 'firebase/auth';
import { AUTH_TOKEN } from './auth.provider';
import { UserRole, ROLE_LEVELS } from './role.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly auth = inject(AUTH_TOKEN);
  
  // 認証状態のSignal
  private _user = signal<User | null | undefined>(undefined);
  private _customClaims = signal<{ role: UserRole | null; organizationId: string | null; siteId: string | null; departmentId: string | null } | null>(null);
  
  // コンポーネント等から参照するRead-only Signal (undefinedは初期化中、nullは未ログイン)
  public readonly user = this._user.asReadonly();
  public readonly isAuthenticated = computed(() => this._user() != null);
  public readonly isAuthResolved = computed(() => this._user() !== undefined);

  public readonly userRole = computed(() => this._customClaims()?.role ?? null);
  public readonly userOrganizationId = computed(() => this._customClaims()?.organizationId ?? null);
  public readonly userSiteId = computed(() => this._customClaims()?.siteId ?? null);
  public readonly userDepartmentId = computed(() => this._customClaims()?.departmentId ?? null);

  constructor() {
    // 認証状態の変更をリッスン
    const unsubscribe = onIdTokenChanged(this.auth, async (user) => {
      if (user) {
        if (this._user()?.uid !== user.uid) {
          this._user.set(undefined);
        }
        try {
          const tokenResult = await user.getIdTokenResult();
          
          // 非同期処理の間にユーザーが切り替わっていないか確認
          if (this.auth.currentUser?.uid !== user.uid) return;

          const claims = tokenResult.claims;
          
          const rawRole = claims['role'];
          const role = typeof rawRole === 'string' && rawRole in ROLE_LEVELS
            ? (rawRole as UserRole)
            : null;

          const rawOrgId = claims['organizationId'];
          const organizationId = typeof rawOrgId === 'string' ? rawOrgId : null;
          
          const rawSiteId = claims['siteId'];
          const siteId = typeof rawSiteId === 'string' ? rawSiteId : null;
          
          const rawDepId = claims['departmentId'];
          const departmentId = typeof rawDepId === 'string' ? rawDepId : null;
          
          this._customClaims.set({
            role,
            organizationId,
            siteId,
            departmentId
          });
        } catch (error) {
          console.error('Failed to get Custom Claims:', error);
          this._customClaims.set({ role: null, organizationId: null, siteId: null, departmentId: null });
        }
        this._user.set(user);
      } else {
        this._customClaims.set(null);
        this._user.set(null);
      }
    });

    inject(DestroyRef).onDestroy(() => unsubscribe());
  }

  // サインイン
  async signIn(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
  }

  // カスタムトークンでサインイン
  async signInWithCustomToken(token: string): Promise<void> {
    await signInWithCustomToken(this.auth, token);
  }

  // サインアウト
  async signOut(): Promise<void> {
    await signOut(this.auth);
  }
}
